import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, X, Camera } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { Button, Select } from "../lib/ui";
import { ROLE_LABELS } from "../lib/constants";
import { useT } from "../lib/i18n";

const PAGE_NAMES = {
  "/": "Dashboard",
  "/orders": "Orders",
  "/orders/new": "New Order",
  "/purchasing": "Purchasing",
  "/delivery": "Delivery & Documents",
  "/receiving": "Receiving",
  "/inventory": "Central Inventory",
  "/ledger": "Stock Movement Ledger",
  "/waste": "Waste Management",
  "/closing": "Daily Closing",
  "/notifications": "Notification Center",
  "/audit": "Audit Timeline",
  "/master-data": "Master Data",
  "/users": "Users & Roles",
  "/feedback": "Feedback Review",
};

const TYPES = [
  { value: "BUG", label: "Bug" },
  { value: "IMPROVEMENT", label: "Improvement" },
  { value: "MISSING_PRODUCT", label: "Missing Product" },
  { value: "WORKFLOW_ISSUE", label: "Workflow Issue" },
  { value: "OTHER", label: "Other" },
];

export default function FeedbackWidget() {
  const { user } = useAuth();
  const toast = useToast();
  const { t } = useT();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("BUG");
  const [notes, setNotes] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [saving, setSaving] = useState(false);

  // English page name is sent to the backend; display is translated.
  const pageName = PAGE_NAMES[location.pathname] ||
    (location.pathname.startsWith("/orders/") ? "Order Detail" : location.pathname);

  const readShot = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error(t("Screenshot too large (max 3MB)"));
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!notes.trim()) return toast.error(t("Please add some notes"));
    setSaving(true);
    try {
      await api.post("/feedback", { feedback_type: type, notes, page: pageName, screenshot });
      toast.success(t("Thanks! Your feedback was sent."));
      setOpen(false);
      setNotes(""); setScreenshot(null); setType("BUG");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Failed to send feedback"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        className="fixed bottom-20 right-4 z-30 flex items-center gap-2 rounded-full bg-ink px-4 py-3 text-sm font-semibold text-white shadow-cardhover transition-all hover:bg-stone-800 active:scale-95 lg:bottom-6 lg:right-6"
      >
        <MessageSquarePlus size={18} /> <span className="hidden sm:inline">{t("Feedback")}</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center sm:p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md animate-fade-up rounded-t-2xl bg-white shadow-cardhover sm:rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="feedback-modal">
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h3 className="font-heading text-lg font-bold">{t("Send Feedback")}</h3>
              <button onClick={() => setOpen(false)} className="rounded-lg p-1 text-ink-mute hover:bg-stone-100" data-testid="feedback-close"><X size={18} /></button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
              {/* Auto-captured context */}
              <div className="mb-4 grid grid-cols-3 gap-2 rounded-xl bg-stone-50 p-3 text-xs">
                <div><p className="text-ink-mute">{t("Page")}</p><p className="font-semibold text-ink" data-testid="fb-page">{t(pageName)}</p></div>
                <div><p className="text-ink-mute">{t("Role")}</p><p className="font-semibold text-ink">{t(ROLE_LABELS[user?.role])}</p></div>
                <div><p className="text-ink-mute">{t("Branch")}</p><p className="font-semibold text-ink">{user?.branch?.name || "—"}</p></div>
              </div>

              <Select label={t("Feedback Type")} value={type} onChange={(e) => setType(e.target.value)} className="mb-3" data-testid="fb-type">
                {TYPES.map((tp) => <option key={tp.value} value={tp.value}>{t(tp.label)}</option>)}
              </Select>

              <div className="mb-3">
                <label className="cf-label">{t("Notes")}</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="cf-input" placeholder={t("Describe the issue or idea...")} data-testid="fb-notes" />
              </div>

              <div>
                <label className="cf-label">{t("Screenshot (optional)")}</label>
                <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-line px-3 py-2.5 text-sm text-ink-soft hover:border-primary">
                  <Camera size={16} /> {screenshot ? t("Change image") : t("Attach a screenshot")}
                  <input type="file" accept="image/*" onChange={readShot} className="hidden" data-testid="fb-screenshot" />
                </label>
                {screenshot && <img src={screenshot} alt="" className="mt-2 h-24 rounded-lg object-cover" />}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-line px-5 py-4">
              <Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button>
              <Button onClick={submit} loading={saving} data-testid="fb-submit">{t("Send Feedback")}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
